import React, { useState } from 'react';
import {
  CircleAlert, Eye, EyeOff, Info, TrendingDown, TrendingUp, TriangleAlert, Trophy,
} from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Tooltip } from '../../components/ui/tooltip';
import MetricSelect from './MetricSelect';
import { formatDuration } from '../../utils/duration';
import { formatMetricLabelled, formatMetricNumber, metricGrade, metricMeta, metricTone } from './metricTone';

// 'neutral' covers the untierable metrics (RMSE, MAE, log loss, QWK): no
// universal good/bad threshold exists, so the number is shown without a verdict.
const TONE_COLOR = {
  green: 'var(--green)',
  error: 'var(--error-500)',
  warn: 'var(--warn-500)',
  neutral: 'var(--foreground)',
};

/**
 * Renders a `model_comparison` block as ONE model's performance.
 *
 * The engine races several methods internally, but to the user that is a single model called QBrain
 * — so this card shows only the winning fit's scores, with a picker over the metrics the run was
 * scored on. The leaderboard lives under `blockData.comparison`, which the backend strips for
 * everyone except staff (see backend/tableyze/block_redaction.py). Its presence is therefore the
 * entire gate for the reveal toggle below: if the data arrived, the viewer is allowed to see it.
 *
 * Legacy rows (engine-specific keys at the top level) are normalized to this shape server-side on
 * read, so this component only ever deals with `metrics` / `primary_metric` / `comparison`.
 */
const ModelComparisonBlock = ({ blockData }) => {
  const {
    metrics = [],
    primary_metric: primaryMetric,
    mode_collapse: modeCollapse = false,
    target_column: targetColumn,
    n_train_samples: nTrain,
    n_test_samples: nTest,
    training_time_sec: trainingTime,
    comparison,
  } = blockData || {};

  const [selected, setSelected] = useState(primaryMetric);
  const [showInternals, setShowInternals] = useState(false);

  const row = metrics.find((m) => m.name === selected) || metrics[0] || {};
  const metricName = row.name || primaryMetric;
  // One source of truth for both: metricGrade is derived from metricTone, so the verdict word and
  // the colour of the number under it always come from the same cut points.
  const grade = metricGrade(row.test, metricName);

  const title = targetColumn ? `QBrain · ${targetColumn.replace(/_/g, ' ')}` : 'QBrain';

  const footerParts = [
    nTrain != null && `${Number(nTrain).toLocaleString()} training rows`,
    nTest != null && `${Number(nTest).toLocaleString()} test rows`,
    formatDuration(trainingTime) && `trained in ${formatDuration(trainingTime)}`,
  ].filter(Boolean);

  const tone = metricTone(row.test, metricName);
  const meta = metricMeta(metricName);

  return (
    <Block
      icon="brain"
      title={title}
      description="Scored on rows held out from training"
      style={{ width: 'fit-content', minWidth: 400, maxWidth: '100%' }}
      footer={footerParts.length ? <span>{footerParts.join(' · ')}</span> : null}
      badge={(
        <>
          <MetricSelect
            options={metrics.map((m) => m.name)}
            value={metricName}
            onChange={setSelected}
          />
          {comparison && (
            <RevealButton active={showInternals} onClick={() => setShowInternals((v) => !v)} />
          )}
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <MetricLabel label={`Test ${meta.label}`} tooltip={meta.tooltip} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            className="q-num"
            style={{
              fontSize: 52, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.035em', color: 'var(--foreground)',
            }}
          >
            {formatMetricNumber(row.test, metricName)}
          </span>
          {grade && (
            <Badge variant={GRADE_VARIANT[tone] || 'secondary'}>
              {tone === 'green' ? <TrendingUp aria-hidden /> : tone === 'error' ? <TrendingDown aria-hidden /> : <CircleAlert aria-hidden />}
              {grade.label} performance
            </Badge>
          )}
        </div>
        {grade?.note && (
          <span className="q-muted" style={{ fontSize: 14.5, lineHeight: 1.5, maxWidth: 380 }}>{grade.note}</span>
        )}
      </div>

      <Separator style={{ margin: '22px 0 16px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <MetricLabel label={`Train ${meta.label}`} tooltip={null} />
        <span className="q-num" style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 500, color: 'var(--foreground)' }}>
          {formatMetricNumber(row.train, metricName)}
        </span>
      </div>

      {modeCollapse && (
        <Badge variant="warning" style={{ marginTop: 12 }}>
          <TriangleAlert aria-hidden />
          Mode collapse detected
        </Badge>
      )}

      {comparison && showInternals && <EngineInternals comparison={comparison} />}
    </Block>
  );
};

const GRADE_VARIANT = { green: 'success', warn: 'warning', error: 'destructive' };

const MetricLabel = ({ label, tooltip }) => (
  <span className="q-stat__label">
    {label}
    {tooltip && (
      <Tooltip content={tooltip}>
        <button
          type="button"
          aria-label={`About ${label}`}
          style={{
            display: 'inline-flex', padding: 0, border: 0, background: 'transparent',
            color: 'var(--muted-foreground)', cursor: 'help',
          }}
        >
          <Info aria-hidden />
        </button>
      </Tooltip>
    )}
  </span>
);

const RevealButton = ({ active, onClick }) => (
  <Tooltip content={active ? 'Hide engine internals' : 'Show engine internals (staff only)'}>
    <Button
      variant="outline"
      size="icon-sm"
      onClick={onClick}
      aria-pressed={active}
      aria-label={active ? 'Hide engine internals' : 'Show engine internals'}
      className={active ? 'ui-btn--active' : undefined}
    >
      {active ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
    </Button>
  </Tooltip>
);

/**
 * The staff-only leaderboard: one card per method the engine actually ran. Reached only when the
 * backend chose to send `comparison`, i.e. the viewer is staff.
 */
const EngineInternals = ({ comparison }) => {
  const {
    engine, winner, winner_method: winnerMethod, winner_effort: winnerEffort,
    metric_name: metricName, candidates,
  } = comparison;

  // QBrain reports one entry per method it routed to; the builtin race has a fixed pair.
  const cards = Array.isArray(candidates) && candidates.length > 0
    ? candidates.map((c, i) => ({
      id: c.method || c.name || i,
      name: c.name || c.method,
      testMetric: c.skipped ? null : c.test_metric,
      trainMetric: c.skipped ? null : c.train_metric,
      isWinner: !!c.is_winner,
      modeCollapse: !!c.mode_collapse,
    }))
    : [
      {
        id: 'xgboost',
        name: 'XGBoost',
        testMetric: comparison.xgb_test_metric,
        trainMetric: comparison.xgb_train_metric,
        isWinner: winner === 'xgboost',
        modeCollapse: false,
      },
      {
        id: 'tabicl',
        name: 'TabICLv2',
        testMetric: comparison.tabicl_test_metric,
        trainMetric: comparison.tabicl_train_metric,
        isWinner: winner === 'tabicl',
        modeCollapse: false,
      },
    ];

  const provenance = [
    engine && `engine: ${engine}`,
    winnerMethod && `winner: ${winnerMethod}`,
    winnerEffort && `effort: ${winnerEffort}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="q-section" style={{ marginTop: 16 }}>
      <Separator style={{ margin: '0 0 6px' }} />
      <div className="q-section__head" style={{ minHeight: 0 }}>
        <span className="q-section__title">Engine internals</span>
        <Badge variant="outline">Staff only</Badge>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {cards.map(({ id, ...card }) => (
          <ModelCard key={id} {...card} metricName={metricName} />
        ))}
      </div>
      {provenance && (
        <div className="q-muted" style={{ fontSize: 14 }}>
          {provenance}
        </div>
      )}
    </div>
  );
};

function ModelCard({ name, testMetric, trainMetric, metricName, isWinner, modeCollapse }) {
  const tone = metricTone(testMetric, metricName);
  return (
    <Card style={{
      width: 200, flexShrink: 0, boxShadow: 'none',
      borderColor: isWinner ? 'var(--foreground)' : undefined,
    }}>
      <CardContent style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="q-truncate" style={{ fontSize: 15, fontWeight: 500, color: 'var(--foreground)', flex: 1 }}>
            {name}
          </span>
          {isWinner && <Badge variant="default"><Trophy aria-hidden />Winner</Badge>}
        </div>
        {testMetric != null ? (
          <>
            <div className="q-num" style={{ fontSize: 22, fontWeight: 500, color: TONE_COLOR[tone] || 'var(--foreground)' }}>
              {formatMetricLabelled(testMetric, metricName)}
            </div>
            <div className="q-muted" style={{ fontSize: 13.5 }}>
              test set · train: {formatMetricLabelled(trainMetric, metricName)}
            </div>
          </>
        ) : (
          <div className="q-muted" style={{ fontSize: 15 }}>Not available</div>
        )}
        {modeCollapse && (
          <Badge variant="warning"><TriangleAlert aria-hidden />Mode collapse</Badge>
        )}
      </CardContent>
    </Card>
  );
}

export default ModelComparisonBlock;
